import { type FC } from 'react';
import { useUnit } from 'effector-react';
import { Pagination, ScrollArea, Stack } from '@mantine/core';
import { type PayoutCascade, type PayoutCascadeFilters, usePayoutCascadesList } from '~/entities/payout';
import { type PaginationModel } from '~/shared/lib/pagination';
import { Table } from '~/shared/ui/Table';
import { createTableData } from './tableUtils';

interface CascadesListProps {
    filter?: PayoutCascadeFilters;
    paginationModel: PaginationModel;
    onClick?: (cascade: PayoutCascade) => void;
}

export const CascadesList: FC<CascadesListProps> = ({ filter, onClick, paginationModel }) => {
    const pagination = useUnit(paginationModel.$pagination);

    const cascadesList = usePayoutCascadesList({
        'pagination.page': pagination.page.toString(),
        'pagination.perPage': pagination.perPage.toString(),
        ...(filter?.name?.value && { 'filter.name.value': filter.name.value }),
        ...(filter?.id?.value && { 'filter.id.value': filter.id.value }),
    });

    const total = Number(cascadesList.data?.pagination?.total);
    const totalPages = Math.ceil(total / pagination.perPage);

    const tableData = createTableData({
        cascades: cascadesList.data?.items,
    });

    const handleRowClick = (item: PayoutCascade) => {
        return onClick?.(item);
    };

    const handleChangeCurrentPage = (page: number) => {
        paginationModel.setPage(page - 1);
    };

    return (
        <Stack>
            <ScrollArea h={512}>
                <Table
                    striped
                    highlightOnHover
                    data={tableData}
                    onRowClick={handleRowClick}
                    loading={cascadesList.isLoading}
                />
            </ScrollArea>

            {totalPages > 1 && (
                <Pagination
                    total={totalPages}
                    value={pagination.page + 1}
                    onChange={handleChangeCurrentPage}
                    size='sm'
                />
            )}
        </Stack>
    );
};
